import React from "react";
import { RouterProvider, createBrowserRouter } from "react-router-dom";
import { FliptProvider } from "@flipt-io/flipt-client-react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import RouterError from "~/components/RouterError";
import { ToastProvider as ShadcnToastProvider } from "~/components/ui/toast";
import { ErrorToastProvider } from "~/contexts/error-toast";
import { AppHeaderProvider } from "~/contexts/header";
import { UserProvider } from "~/contexts/user";
import { useFetcher } from "~/hooks/useFetcher";
import useRecordPageView from "~/hooks/useRecordPageView";
import routes from "~/routes";
import theme from "~/theme";
import User from "~/types/interfaces/user";
import { identify } from "~/utils/monitoring";
import { ConversationsProvider } from "./contexts/conversations";

const { VITE_FLIPT_API } = import.meta.env;

const App = () => {
  useRecordPageView();
  const { data: user } = useFetcher<User>("/me", {
    keepPreviousData: true,
    onSuccess: (user) => {
      identify(user);
    },
  });

  const router = createBrowserRouter(
    routes.map((route) => ({
      ...route,
      // ID is necessary for useRouteError in RouterError
      id: route.path,
      errorElement: <RouterError />,
    }))
  );

  return (
    <React.StrictMode>
      <FliptProvider
        options={{
          environment: "default",
          namespace: "default",
          url: VITE_FLIPT_API,
          updateInterval: 600,
        }}
      >
        <UserProvider user={user ?? null}>
          <ConversationsProvider>
            <ErrorToastProvider>
              <ShadcnToastProvider>
                <AppHeaderProvider>
                  <ThemeProvider theme={theme}>
                    <CssBaseline />
                    <RouterProvider router={router} />
                  </ThemeProvider>
                </AppHeaderProvider>
              </ShadcnToastProvider>
            </ErrorToastProvider>
          </ConversationsProvider>
        </UserProvider>
      </FliptProvider>
    </React.StrictMode>
  );
};

export default App;
